import { assert } from '@ember/debug';
import { getOwner, setOwner } from '@ember/owner';
import { CacheHandler, Fetch, RequestManager } from '@warp-drive/core';
import { LegacyNetworkHandler } from '@warp-drive/legacy/compat';
import type { Handler, NextFn } from '@warp-drive/core/request';
import type { RequestContext } from '@warp-drive/core/types/request';
import {
  TioImpersonationHandler,
  type ImpersonationHandlerOptions,
} from './handlers/impersonation-handler.ts';
import { TioResponseHandler, type ResponseHandlerOptions } from './handlers/response-handler.ts';

const DEFAULT_RENEW_THRESHOLD_MS = 1800000;
const DEFAULT_RENEWAL_COOLDOWN_MS = 5000;
const DEFAULT_RENEWAL_FAILURE_COOLDOWN_MS = 60000;

const defaultTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || '';
const defaultRequestId = () => crypto.randomUUID();

type TioRequestSession = {
  isAuthenticated?: boolean;
  data?: {
    authenticated?: {
      access_token?: string;
    };
  };
  expireAt?: number;
  renew?: (token?: string) => Promise<unknown> | unknown;
};

export type TioSessionRenewalOptions = {
  passToken?: boolean;
  thresholdMs?: number;
  cooldownMs?: number;
  failureCooldownMs?: number;
};

export type TioRequestManagerOptions = {
  apiKey: string;
  contentType: string;
  accept?: string;
  jsonApiKeyFormat?: string;
  sessionRenewal?: TioSessionRenewalOptions;
  timeZone?: () => string;
  requestId?: () => string;
  /**
   * Returns the AuthKey#public_key the current app should send as `x-auth-key`,
   * or undefined if none is known yet (e.g. before pre-registration completes).
   * Called once per request so changes to the underlying service propagate
   * without rebuilding the request manager.
   */
  authKey?: () => string | undefined;
  /** When set, gates mutating requests during impersonated sessions. */
  impersonation?: ImpersonationHandlerOptions;
  /** When set, mirrors ApplicationAdapter#ensureResponseAuthorized for builder requests. */
  response?: ResponseHandlerOptions;
};

class TioAuthRequestHandler {
  private nextRenewalAllowedAt = 0;

  constructor(private options: TioRequestManagerOptions) {}

  async request<T>(context: RequestContext, next: NextFn<T>) {
    const { request } = context;
    const headers = new Headers(request.headers);

    // Defaults — `setIfMissing` so a builder that explicitly sets
    // Content-Type (e.g. a JSON-bodied test endpoint inside a vnd.api+json
    // app) wins. Previously these used `.append`, which double-set the
    // header to "<builder>, <default>" and broke the override.
    const setIfMissing = (name: string, value: string) => {
      if (!headers.has(name)) headers.set(name, value);
    };

    setIfMissing('Content-Type', this.options.contentType);
    if (this.options.accept) {
      setIfMissing('Accept', this.options.accept);
    }
    setIfMissing('x-api-key', this.options.apiKey);
    const authKey = this.options.authKey?.();
    if (authKey) {
      // `setIfMissing` so a builder that explicitly sets `x-auth-key` (e.g.
      // a special service-to-service flow) still wins over the session's
      // default — same contract as the other headers above.
      setIfMissing('x-auth-key', authKey);
    }
    if (this.options.jsonApiKeyFormat) {
      setIfMissing('x-json-api-key-format', this.options.jsonApiKeyFormat);
    }
    setIfMissing('Time-Zone', this.timeZone());
    setIfMissing('X-Request-Id', this.requestId());

    const session = this.session;
    const accessToken = session.data?.authenticated?.access_token;
    if (session.isAuthenticated && accessToken) {
      headers.set('tio-auth-token', accessToken);
      this.maybeRenewSession();
    }

    return next({ ...request, headers });
  }

  private get session() {
    const owner = getOwner(this);
    assert('Expected owner to be available for request auth handler', owner);

    const session = owner.lookup('service:session');
    assert('Expected service:session to be registered', session);

    return session as TioRequestSession;
  }

  private timeZone() {
    return this.options.timeZone?.() ?? defaultTimeZone();
  }

  private requestId() {
    return this.options.requestId?.() ?? defaultRequestId();
  }

  private maybeRenewSession() {
    if (!this.options.sessionRenewal) {
      return;
    }

    const now = Date.now();
    const cooldownMs = this.options.sessionRenewal.cooldownMs ?? DEFAULT_RENEWAL_COOLDOWN_MS;
    if (now < this.nextRenewalAllowedAt) {
      return;
    }

    const session = this.session;
    const expiresAt = session.expireAt;
    if (!expiresAt) {
      return;
    }

    const timeLeft = expiresAt - now;
    const thresholdMs = this.options.sessionRenewal.thresholdMs ?? DEFAULT_RENEW_THRESHOLD_MS;

    if (timeLeft < thresholdMs && timeLeft > 0) {
      this.nextRenewalAllowedAt = now + cooldownMs;

      void (async () => {
        try {
          if (!session.isAuthenticated) {
            return;
          }

          const currentToken = session.data?.authenticated?.access_token;
          if (!currentToken) {
            return;
          }

          if (!session.renew) {
            throw new Error(
              'Session renewal is configured but service:session has no renew method'
            );
          }

          if (this.options.sessionRenewal?.passToken === false) {
            await session.renew();
          } else {
            await session.renew(currentToken);
          }
        } catch (error) {
          const failureCooldownMs =
            this.options.sessionRenewal?.failureCooldownMs ?? DEFAULT_RENEWAL_FAILURE_COOLDOWN_MS;
          this.nextRenewalAllowedAt = Date.now() + failureCooldownMs;
          console.error('Failed to renew session:', error);
        }
      })();
    }
  }
}

export function createTioRequestManager(ownedObject: object, options: TioRequestManagerOptions) {
  const owner = getOwner(ownedObject);
  assert('Expected owner to be available for request manager setup', owner);

  const authHandler = new TioAuthRequestHandler(options);
  setOwner(authHandler, owner);

  // Order matters. LegacyNetworkHandler short-circuits adapter-routed
  // requests (they keep using ApplicationAdapter#ajax/handleResponse).
  // For builder requests, the response handler wraps everything below
  // it so it can observe errors thrown by Fetch; the impersonation
  // handler then gates the outgoing request before auth headers are
  // applied.
  const handlers: Handler[] = [LegacyNetworkHandler];
  if (options.response) {
    handlers.push(new TioResponseHandler(options.response));
  }
  if (options.impersonation) {
    handlers.push(new TioImpersonationHandler(options.impersonation));
  }
  handlers.push(authHandler, Fetch);

  return new RequestManager().use(handlers).useCache(CacheHandler);
}
